import React, { useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import { Typography, Button, Modal } from '@deeptrue-frontend/ui';
import { ProjectStatus } from 'consts/statuses';
import intl, { useFormatMessage } from 'components/IntlProvider';
import { ReactComponent as Eye } from 'assets/icons/eye.svg';
import env from 'consts/env';
import BexioStartModal from './BexioStartModal';
import api from 'api/api';

const BexioModal = styled(Modal)`
  max-width: 550px;
`;

const StyledHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0;
  margin-top: 2.31rem;
`;

const StyledHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StyledButton = styled(Button)`
  padding: 0.5rem 1rem;
  line-height: 1;
  border: 1px solid #e3e5e8;
  display: flex;
  align-items: center;
  &:hover {
    svg {
      fill: #0055ab;
    }
  }
`;

const IconWrapper = styled.div`
  margin-right: 0.688rem;
  display: flex;
  align-items: center;
`;

const StickyButton = styled(Button)<{ isBrandValuator?: boolean }>`
  position: fixed;
  bottom: 0.5rem;
  right: ${({ isBrandValuator }) => (isBrandValuator ? '3.5rem' : '30rem')};
  box-shadow: 0px 4px 4px rgba(0, 0, 0, 0.25);
  z-index: 12;
`;

const StyledTypography = styled(Typography)``;

interface StatusType {
  value: number;
  display: string;
}

interface Props {
  updateProjectStatus: (statusName: 'edit' | 'accept') => void;
  status: StatusType;
  isProcessing: boolean;
  surveyUuid?: string;
  updateValues: (data: any) => void;
  isBexioUser: boolean;
  projectId: number;
  organization: string;
  projectUuid: string;
  isBrandValuatorProduct: boolean;
  generateReport: () => void;
  isGeneratingReport: boolean;
  history: any;
  isSubscribableProject: boolean;
  routePush: string;
}

const Header = ({
  surveyUuid,
  status,
  projectUuid,
  isBexioUser,
  projectId,
  organization,
  isBrandValuatorProduct,
  generateReport,
  isGeneratingReport,
  history,
  routePush,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [uuid, setUuid] = useState(surveyUuid);
  const { locale } = intl.useIntl();
  const t = useFormatMessage();

  useEffect(() => {
    if (!uuid) {
      api.resources
        .getProjectSurveys({
          orgName: organization,
          uuid: projectUuid,
        })
        .then(({ data }) => {
          const isUuidExist = data.results[0];
          if (isUuidExist) {
            setUuid(data.results[0].uuid);
          }
        });
    }
    // eslint-disable-next-line
  }, [status.value]);

  const isEditMode = status.value === ProjectStatus.OFFER_DRAFT;

  const areActionsDisabled =
    status.value === ProjectStatus.OFFER_ARCHIVED ||
    status.value === ProjectStatus.FINISHED ||
    status.value === ProjectStatus.COLLECTING_DATA;

  const isSurveyUUID = uuid && uuid !== '';
  const isBrandValuatorReportGenerated =
    status.value === ProjectStatus.ANALYSIS_DONE;

  const getCTA = () => {
    if (isBrandValuatorProduct) {
      return isBrandValuatorReportGenerated ? (
        <StickyButton
          id="start-data-collection"
          isLoading={isGeneratingReport}
          disabled={areActionsDisabled}
          style={{ whiteSpace: 'nowrap' }}
          ml="1rem"
          onClick={() => history.push(routePush)}
          variant="primary"
          isBrandValuator={isBrandValuatorProduct}
        >
          {t('project.testEdit.viewReport', 'View Report')}
        </StickyButton>
      ) : (
        <StickyButton
          id="start-data-collection"
          isLoading={isGeneratingReport}
          disabled={areActionsDisabled}
          style={{ whiteSpace: 'nowrap' }}
          ml="1rem"
          onClick={generateReport}
          variant="primary"
          isBrandValuator={isBrandValuatorProduct}
        >
          {t('project.testEdit.generateReport', 'Generate Report')}
        </StickyButton>
      );
    }
    switch (status.value) {
      case ProjectStatus.FINISHED:
        return (
          <StickyButton
            data-testid="go_to_data_collection"
            onClick={() => history.push(routePush)}
            ml="0.5rem"
          >
            {t('project.testEdit.viewReport', 'View Report')}
          </StickyButton>
        );
      case ProjectStatus.COLLECTING_DATA:
        return (
          <StickyButton
            data-testid="go_to_data_collection"
            onClick={() => history.push(routePush)}
            ml="0.5rem"
          >
            {t('project.testEdit.viewReport', 'View Report')}
          </StickyButton>
        );
      case ProjectStatus.OFFER_ACCEPTED:
      case ProjectStatus.OFFER_DRAFT:
        return isBexioUser ? (
          <StickyButton
            id="start-data-collection"
            disabled={areActionsDisabled}
            style={{ whiteSpace: 'nowrap' }}
            ml="1rem"
            onClick={() => setIsModalOpen(true)}
            variant="primary"
          >
            {t('project.testEdit.startCollecting', 'Start Data Collection')}
          </StickyButton>
        ) : (
          <StickyButton
            data-testid="go_to_data_collection"
            onClick={() => history.push(routePush)}
            ml="0.5rem"
          >
            {t('project.testEdit.startCollecting', 'Start Data Collection')}
          </StickyButton>
        );
    }
  };

  return (
    <StyledHeaderContainer>
      <StyledHeader>
        <StyledTypography fontSize="1.125rem" color="#353531" fontWeight="500">
          {t('project.review.title', 'Setup')}
        </StyledTypography>
        <ButtonsWrapper>
          {!isEditMode && uuid && !isBrandValuatorProduct && (
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={`${env.APP_SURVEY_URL}/test/${uuid}/?user_lang=${locale}`}
              style={{
                textDecoration: 'none',
              }}
            >
              <StyledButton variant="secondary" isLoading={!isSurveyUUID}>
                <IconWrapper>
                  <Eye />
                </IconWrapper>
                {t('project.testEdit.preview', 'Preview Questionnaire')}
              </StyledButton>
            </a>
          )}
          {getCTA()}
        </ButtonsWrapper>
        <BexioModal
          isVisible={isModalOpen}
          onCloseClick={() => setIsModalOpen(false)}
          onBackdropClick={() => setIsModalOpen(false)}
        >
          <BexioStartModal
            closeModal={() => setIsModalOpen(false)}
            initialValues={{
              terms: false,
              terms_agreement: false,
              privacy_policy_accepted: false,
            }}
            projectId={projectId}
            surveyUuid={surveyUuid}
            organization={organization}
          />
        </BexioModal>
      </StyledHeader>
      <Typography
        variant="h6"
        mt="0.5rem"
        mb="1.5rem"
        fontSize="0.875rem"
        fontWeight="400"
        lineHeight="1.25rem"
        color="#353531"
      >
        {t(
          'project.review.subtitle',
          'Review the project settings and preview the questionnaire for respondents before starting data collection.'
        )}
      </Typography>
    </StyledHeaderContainer>
  );
};

export default Header;
